import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API_BASE, kcFetch, authHeaders, getToken } from "./apiClient";

// ============================================================
// ThemeContext — per-user theme preference (dark | light).
// • Phase-4: default is LIGHT. Dark mode uses the legacy
//   Kingdom Connect gold-on-charcoal identity.
// • Persists to server via PUT /api/me/preferences once the user
//   is signed in, and locally to localStorage as a fast cache so
//   the next page load applies instantly with no flash.
// • localStorage key: `kc_theme_mode` (spec). We still read the
//   old `kc_theme` key for one-time migration.
// ============================================================
const KEY = "kc_theme_mode";
const LEGACY_KEY = "kc_theme";

function readInitialTheme() {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark") return v;
    // One-time migration from the pre-Phase-4 key.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "light" || legacy === "dark") {
      localStorage.setItem(KEY, legacy);
      return legacy;
    }
  } catch (_) {}
  return "light";
}

const Ctx = createContext({ theme: "light", setTheme: () => {}, toggle: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);

  // Apply theme class on <body> so global CSS variables flip cleanly.
  // Also toggles `.dark` on <html> so the Phase-1 tokens.css
  // dark-mode overrides (which key off `.dark`) activate correctly
  // while preserving the legacy `body.theme-light` / `body.theme-dark`
  // class contract used by the older CSS.
  useEffect(() => {
    try {
      document.body.classList.toggle("theme-light", theme === "light");
      document.body.classList.toggle("theme-dark", theme === "dark");
      document.documentElement.classList.toggle("dark", theme === "dark");
      localStorage.setItem(KEY, theme);
    } catch (_) {}
  }, [theme]);

  const setTheme = useCallback(async (next) => {
    setThemeState(next);
    if (!getToken()) return;
    try {
      await kcFetch(`${API_BASE}/me/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ theme: next }),
      });
    } catch (_) { /* offline-tolerant */ }
  }, []);

  const toggle = useCallback(() => setTheme(theme === "light" ? "dark" : "light"), [theme, setTheme]);

  return <Ctx.Provider value={{ theme, setTheme, toggle }}>{children}</Ctx.Provider>;
}

export function useTheme() { return useContext(Ctx); }

// Apply the user's saved server-side theme after login. Called from
// the auth-restore flow once we have currentUser.
export function syncThemeFromUser(user, setTheme) {
  const t = user?.preferences?.theme;
  if (t === "light" || t === "dark") setTheme(t);
}
